"use client";

import { useState, useRef, useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  Menu,
  ChevronDown,
  Handshake,
  Bot,
  Store,
  ExternalLink,
  ArrowRight,
  Sparkles,
  Globe,
  TrendingUp,
  Users,
} from "lucide-react";
import { Container } from "@/components/ui/container";
import { Badge } from "@/components/ui/badge";
import { BuddifyLogo } from "@/components/ui/buddify-logo";
import { MobileNav } from "./mobile-nav";
import { PageSubNav } from "./page-sub-nav";
import { findMostSpecificPathMatch } from "./page-sub-nav-utils";
import { CartNavLink } from "@/components/community/cart/cart-nav-link";
import { CommunityWordmark } from "@/components/community/community-wordmark";
import { useHeaderScroll } from "./header-scroll-context";
import {
  StartFreeLauncherContent,
  StartFreeTrigger,
} from "./start-free-launcher";
import { cn } from "@/lib/utils";
import { subNavHeaderConfig, productStats } from "@/lib/constants";

const products = [
  {
    name: "Brand Partnerships & Partner Dropshipping",
    tagline: "Expand Your Business",
    description:
      "Connect with partner stores for dropshipping, wholesale, and marketplace sales",
    href: "/brand-partnerships",
    icon: Handshake,
    color: "primary" as const,
    stats: `${productStats.brandPartnerships.brands} brands`,
    statsIcon: Globe,
  },
  {
    name: "Buddy AI",
    tagline: "Convert More Visitors",
    description:
      "AI Sales Agent, AI Support Agent, AI Conversion Agent, lead capture, and product Q&A",
    href: "/buddy-ai-assistant",
    icon: Bot,
    badge: "Built for Shopify",
    color: "secondary" as const,
    stats: `${productStats.aiAssistant.conversionIncrease} more conversions`,
    statsIcon: TrendingUp,
  },
  {
    name: "Marketplace",
    tagline: "Sell Direct to Customers",
    description:
      "List your products on the Buddify marketplace and reach new customers",
    href: "/marketplace",
    icon: Store,
    color: "tertiary" as const,
    stats: `${productStats.marketplace.products} products`,
    statsIcon: Users,
  },
];

const mainNav = [
  { name: "Partners", href: "/partners" },
  { name: "Pricing", href: "/pricing" },
  { name: "Contact", href: "/contact" },
  { name: "Help Center", href: "https://support.buddify.app", external: true },
];

function useGlobalBarHidden(enabled: boolean) {
  const [hidden, setHidden] = useState(false);
  const lastScrollY = useRef(0);

  useEffect(() => {
    if (!enabled) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Clear scroll-derived state when navigation disables the collapsing header.
      setHidden(false);
      return;
    }

    const onScroll = () => {
      const currentY = window.scrollY;
      const delta = currentY - lastScrollY.current;

      // Always show the global bar near the top of the page.
      // Reveal on even small upward scrolls so slow scrolls don't keep it hidden.
      if (currentY < 60) {
        setHidden(false);
      } else if (delta > 2) {
        setHidden(true);
      } else if (delta < -2) {
        setHidden(false);
      }

      lastScrollY.current = currentY;
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [enabled]);

  return hidden;
}

function useActiveSubNavConfig() {
  const pathname = usePathname();
  return findMostSpecificPathMatch(pathname, subNavHeaderConfig);
}

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [tryNowOpen, setTryNowOpen] = useState(false);
  const tryNowRef = useRef<HTMLDivElement>(null);
  const tryNowTriggerRef = useRef<HTMLButtonElement>(null);
  const pathname = usePathname();
  const activeConfig = useActiveSubNavConfig();
  const { setGlobalHidden } = useHeaderScroll();
  const isDark = activeConfig?.theme === "dark";
  const hasLocalSubNav = pathname.startsWith("/community/");
  const globalHidden = useGlobalBarHidden(
    Boolean(activeConfig) || hasLocalSubNav,
  );

  useEffect(() => {
    setGlobalHidden(globalHidden);
  }, [globalHidden, setGlobalHidden]);

  // Close the launcher when clicking outside or pressing Escape.
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        tryNowRef.current &&
        !tryNowRef.current.contains(event.target as Node)
      ) {
        setTryNowOpen(false);
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setTryNowOpen(false);
        tryNowTriggerRef.current?.focus();
      }
    }

    if (tryNowOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleKeyDown);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        document.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [tryNowOpen]);

  const isActive = (href: string) => {
    if (href === "/") return pathname === "/";
    return pathname === href || pathname.startsWith(href + "/");
  };

  const isProductsActive = products.some((p) => isActive(p.href));

  // Both bars live inside this container. On product pages, scrolling down
  // translates the whole container up by the height of the global bar,
  // leaving the product bar pinned at the top. The background lives on the
  // inner container so it moves with the bars.
  const headerBase = "fixed top-0 left-0 right-0 z-50";

  const headerInnerClasses = cn(
    "transition-transform duration-300 ease-out will-change-transform border-b lg:backdrop-blur-md",
    isDark
      ? "bg-panel/95 text-brand-green border-line"
      : "bg-panel/80 text-ink border-line/50",
    globalHidden ? "-translate-y-16 md:-translate-y-20" : "translate-y-0"
  );

  const mutedLinkClasses = cn(
    "transition-colors",
    isDark
      ? "text-ink hover:text-ink/80"
      : "text-muted hover:text-ink"
  );

  const activeLinkClasses = isDark ? "text-brand-violet" : "text-brand-green";

  // Reserve the same height in document flow so fixed header doesn't overlap content.
  // Match the header background so no visual seam appears during the slide animation.
  const headerSpacerClasses = cn(
    "w-full shrink-0",
    activeConfig ? "h-[120px] md:h-[136px]" : "h-16 md:h-20",
    "bg-panel"
  );

  return (
    <>
      <header className={headerBase}>
        <div className={headerInnerClasses}>
        {/* Global navigation bar */}
        <div className="h-16 md:h-20">
          <Container className="h-full">

            <nav className="grid grid-cols-[auto_1fr] lg:grid-cols-[auto_1fr_auto] items-center h-full gap-4">
              {/* Logo */}
              <div className="flex items-center gap-3">
                <BuddifyLogo variant="auto" />

                <Link
                  href="/community"
                  aria-label="Buddify Community"
                  className={cn(
                    "hidden min-[480px]:inline-flex lg:hidden xl:inline-flex transition-[transform,box-shadow] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2 motion-reduce:transform-none",
                    isActive("/community")
                      ? "translate-x-0.5 translate-y-0.5 shadow-none"
                      : "shadow-[3px_3px_0_#173c17] hover:translate-x-px hover:translate-y-px hover:shadow-[1px_1px_0_#173c17] active:translate-x-0.5 active:translate-y-0.5 active:shadow-none",
                  )}
                >
                  <CommunityWordmark className="px-2.5 py-1.5 text-[10px] tracking-[0.12em]" />
                </Link>
              </div>

              {/* Desktop Navigation */}
              <div className="hidden lg:flex justify-center items-center gap-4 xl:gap-8">
              <div
                className="relative"
                onMouseEnter={() => setProductsOpen(true)}
                onMouseLeave={() => setProductsOpen(false)}
              >
                <button
                  className={cn(
                    "flex items-center gap-1 font-medium transition-colors",
                    isProductsActive
                      ? activeLinkClasses
                      : mutedLinkClasses
                  )}
                >
                  Our Products
                  <ChevronDown
                    className={cn(
                      "w-4 h-4 transition-transform duration-200",
                      productsOpen && "rotate-180"
                    )}
                  />
                </button>

                {/* Dropdown - Card style (Buddy AI card is dark, others light) */}
                <div
                  className={cn(
                    "absolute top-full left-1/2 -translate-x-1/2 pt-4 transition-all duration-300",
                    productsOpen
                      ? "opacity-100 visible translate-y-0"
                      : "opacity-0 invisible -translate-y-2"
                  )}
                >
                  <div className="bg-panel rounded-2xl shadow-2xl border border-line/80 p-4 w-[520px]">
                    <div className="grid grid-cols-1 gap-3">
                      {products.map((product) => {
                        const isDarkCard = product.color === "secondary";

                        return (
                          <Link
                            key={product.name}
                            href={product.href}
                            onClick={() => setProductsOpen(false)}
                            className={cn(
                              "group relative flex items-start gap-4 p-4 rounded-xl border transition-all duration-200",
                              "hover:shadow-lg hover:-translate-y-0.5",
                              !isDarkCard &&
                                product.color === "primary" &&
                                "border-brand-green-line hover:border-brand-green-line hover:bg-brand-green-soft/50",
                              !isDarkCard &&
                                product.color === "tertiary" &&
                                "border-brand-blue-line hover:border-brand-blue-line hover:bg-brand-blue-soft/50",
                              isDarkCard &&
                                "bg-panel border-line hover:border-brand-violet-line hover:bg-raised"
                            )}
                          >
                            {/* Icon */}
                            <div
                              className={cn(
                                "shrink-0 p-3 rounded-xl",
                                !isDarkCard &&
                                  product.color === "primary" &&
                                  "bg-brand-green-soft text-brand-green",
                                !isDarkCard &&
                                  product.color === "tertiary" &&
                                  "bg-brand-blue-soft text-brand-blue",
                                isDarkCard &&
                                  "bg-brand-violet-soft text-brand-violet"
                              )}
                            >
                              <product.icon className="w-6 h-6" />
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-2">
                                <span
                                  className={cn(
                                    "font-heading font-semibold",
                                    isDarkCard ? "text-ink" : "text-ink"
                                  )}
                                >
                                  {product.name}
                                </span>
                                {product.badge && (
                                  <Badge
                                    variant="secondary"
                                    className={cn(
                                      "text-[10px] py-0 px-1.5",
                                      isDarkCard &&
                                        "bg-raised text-ink/90 border-transparent"
                                    )}
                                  >
                                    <Sparkles className="w-2.5 h-2.5 mr-0.5" />
                                    {product.badge}
                                  </Badge>
                                )}
                              </div>
                              <p
                                className={cn(
                                  "text-xs font-medium mb-2",
                                  !isDarkCard &&
                                    product.color === "primary" &&
                                    "text-brand-green",
                                  !isDarkCard &&
                                    product.color === "tertiary" &&
                                    "text-brand-blue",
                                  isDarkCard && "text-brand-violet"
                                )}
                              >
                                {product.tagline}
                              </p>
                              <p
                                className={cn(
                                  "text-sm line-clamp-2",
                                  isDarkCard ? "text-muted" : "text-muted"
                                )}
                              >
                                {product.description}
                              </p>

                              {/* Stats & Arrow */}
                              <div className="flex items-center justify-between mt-3">
                                <span
                                  className={cn(
                                    "text-xs font-medium flex items-center gap-1",
                                    isDarkCard
                                      ? "text-muted"
                                      : "text-muted"
                                  )}
                                >
                                  {product.statsIcon && (
                                    <product.statsIcon className="w-3 h-3" />
                                  )}
                                  {product.stats}
                                </span>
                                <ArrowRight
                                  className={cn(
                                    "w-4 h-4 transition-all group-hover:translate-x-1",
                                    isDarkCard
                                      ? "text-muted group-hover:text-muted"
                                      : "text-muted group-hover:text-muted"
                                  )}
                                />
                              </div>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

              {/* Main links */}
              {mainNav.map((item) =>
                item.external ? (
                  <a
                    key={item.name}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(
                      "font-medium transition-colors flex items-center gap-1",
                      mutedLinkClasses
                    )}
                  >
                    {item.name}
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                ) : (
                  <Link
                    key={item.name}
                    href={item.href}
                    className={cn(
                      "font-medium transition-colors",
                      isActive(item.href)
                        ? activeLinkClasses
                        : mutedLinkClasses
                    )}
                  >
                    {item.name}
                  </Link>
                )
              )}
            </div>

            {/* CTA + Mobile menu */}
            <div className="flex items-center justify-end gap-2 lg:gap-3">
              <div className="hidden lg:flex items-center gap-3">
                <CartNavLink className={cn("p-2", mutedLinkClasses)} />
                <div className="relative" ref={tryNowRef}>
                  <StartFreeTrigger
                    ref={tryNowTriggerRef}
                    controlsId="start-free-launcher-desktop"
                    expanded={tryNowOpen}
                    onClick={() => setTryNowOpen(!tryNowOpen)}
                  />

                  {/* Popover */}
                  <div
                    id="start-free-launcher-desktop"
                    className={cn(
                      "absolute right-0 top-full mt-2 w-[360px] transition-all duration-200 motion-reduce:transform-none motion-reduce:transition-none",
                      tryNowOpen
                        ? "opacity-100 visible translate-y-0"
                        : "opacity-0 invisible -translate-y-2 pointer-events-none"
                    )}
                  >
                    <StartFreeLauncherContent
                      className="shadow-2xl shadow-primary-950/10"
                      onNavigate={() => setTryNowOpen(false)}
                    />
                  </div>
                </div>
              </div>

              {/* Cart — mobile only; desktop instance sits left of Start for Free */}
              <CartNavLink className={cn("p-2 lg:hidden", mutedLinkClasses)} />

              {/* Mobile menu button */}
              <button
                type="button"
                className="lg:hidden p-2 -mr-2 min-w-11 min-h-11 inline-flex items-center justify-center"
                onClick={() => setMobileMenuOpen(true)}
                aria-label="Open menu"
                aria-expanded={mobileMenuOpen}
                aria-controls="mobile-nav"
              >
                <Menu className="w-6 h-6 text-current" />
              </button>
            </div>
          </nav>
        </Container>
      </div>

      {activeConfig && (
        <PageSubNav
          title={activeConfig.name}
          titleHref={activeConfig.href}
          links={activeConfig.links}
          cta={"cta" in activeConfig ? activeConfig.cta : undefined}
          theme={activeConfig.theme}
          backHref={
            "backHref" in activeConfig && typeof activeConfig.backHref === "string"
              ? activeConfig.backHref
              : undefined
          }
          backLabel={
            "backLabel" in activeConfig && typeof activeConfig.backLabel === "string"
              ? activeConfig.backLabel
              : undefined
          }
          onLinkClick={(href) => {
            if (href !== "#products") return false;
            const el = document.querySelector("#products");
            if (!el) return false;
            const offset = globalHidden ? 76 : 140;
            const top = el.getBoundingClientRect().top + window.scrollY - offset;
            const reduceMotion = window.matchMedia(
              "(prefers-reduced-motion: reduce)",
            ).matches;
            window.scrollTo({
              top,
              behavior: reduceMotion ? "auto" : "smooth",
            });
            return true;
          }}
        />
      )}
      </div>

      <MobileNav
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
      />
    </header>
    <div aria-hidden="true" className={headerSpacerClasses} />
    </>
  );
}
